import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Bot, Radio } from "lucide-react";
import { useApi, useRealtimeEvent, type AgentRun, type AgentStep } from "@ncp/api-client";
import { formatDateTime, useEnumLabel, useLocale, useTranslation } from "@ncp/i18n";
import { AgentActivityFeed, Badge, Card, CardContent, CardHeader, CardTitle, StatusBadge, InfoHint } from "@ncp/ui";

interface LiveStep extends AgentStep { agentKey: string; caseId?: string | null }

/** Staff-wide stream of agent steps (SignalR) with the most recent runs underneath. */
export function LiveAgentFeed({ maxSteps = 24 }: { maxSteps?: number }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const enumLabel = useEnumLabel();
  const api = useApi();
  const navigate = useNavigate();
  const [live, setLive] = useState<LiveStep[]>([]);
  const runs = useQuery({ queryKey: ["recent-runs"], queryFn: () => api.get<AgentRun[]>("/api/ai/runs", { take: 8 }), refetchInterval: 15_000 });
  useRealtimeEvent<{ runId: string; caseId?: string | null; agentKey: string; step?: AgentStep }>("agent.step", (p) => {
    if (!p.step || p.step.kind === "Token") return;
    setLive((prev) => [...prev.filter((s) => s.id !== p.step!.id), { ...p.step!, runId: p.runId, agentKey: p.agentKey, caseId: p.caseId }].slice(-maxSteps));
  });

  return (
    <Card className="flex flex-col">
      <CardHeader className="space-y-1.5 p-6 pb-4">
        <CardTitle className="flex items-center gap-2 text-lg"><Radio className="size-4 text-aegold-700" /> {t("backoffice:commandCenter.liveAgents")} <Badge variant="ai" size="sm" dot pulse>live</Badge><InfoHint>{t("backoffice:commandCenter.liveAgentsHint")}</InfoHint></CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 p-6 pt-0">
        {live.length ? <AgentActivityFeed steps={live.map((s) => ({ ...s, title: `${s.title}` }))} compact /> : <p className="rounded-xl border border-dashed border-sand-300 p-4 text-center text-xs text-muted-foreground">{t("backoffice:commandCenter.liveAgentsIdle")}</p>}
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:commandCenter.recentRuns")}</p>
          <ul className="space-y-1.5">
            {(runs.data ?? []).map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => r.caseId && navigate(`/cases/${r.caseId}?tab=agents`)} className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-start text-sm hover:bg-sand-50">
                  <Bot className="size-3.5 text-aegold-700" />
                  <span className="flex-1 truncate"><span className="font-medium">{r.agentName}</span>{r.caseNumber ? <span className="text-muted-foreground"> · {r.caseNumber}</span> : null}</span>
                  <span className="text-muted-foreground">{formatDateTime(r.startedAt, locale)}</span>
                  <StatusBadge status={r.status === "Succeeded" ? "Approved" : r.status === "Failed" ? "Rejected" : "UnderReview"} label={enumLabel("runStatus", r.status)} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
